import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText, Plus, Trash2, Copy, Save, ArrowLeft, Loader2, X,
  Calculator, AlertCircle, Package, Search, Beaker, Factory,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface Ficha {
  id: number
  produto_id: number
  descricao: string | null
  categoria: string | null
  tempo_producao_min: number
  rendimento_kg: string
  versao: number
  revisao: string
  ativa: boolean
  observacoes: string | null
}

interface Variante {
  variante: string
  maquina_id: number | null
  qtd_ingredientes: number
}

interface Ingrediente {
  id: number
  ficha_id: number
  variante: string
  maquina_id: number | null
  materia_prima_id: number
  mp_codigo: string
  mp_nome: string
  quantidade: string
  unidade: string
  custo_unitario: string
  custo_total: string
  perda_pct: string
  ordem: number
  observacao: string | null
}

interface CustoInfo {
  qtd_ingredientes: number
  qtd_total_kg: string
  custo_total: string
  rendimento_kg: string
  custo_por_kg_base_rendimento: string
  custo_por_kg_base_qtd: string
}

export default function IndFichaEditorPage() {
  const { id } = useParams<{ id: string }>()
  const fichaId = Number(id)
  const qc = useQueryClient()
  const [varianteAtiva, setVarianteAtiva] = useState<string>('PADRAO')
  const [addModal, setAddModal] = useState(false)
  const [copiarModal, setCopiarModal] = useState(false)

  const { data: ficha } = useQuery<Ficha>({
    queryKey: ['ficha', fichaId],
    queryFn: async () => (await api.get(`/industrial/fichas/${fichaId}`)).data,
    enabled: !!fichaId,
  })

  const { data: variantes = [] } = useQuery<Variante[]>({
    queryKey: ['ficha-variantes', fichaId],
    queryFn: async () => (await api.get(`/industrial/fichas/${fichaId}/variantes`)).data,
    enabled: !!fichaId,
  })

  const { data: ingredientes = [], isLoading } = useQuery<Ingrediente[]>({
    queryKey: ['ficha-comp', fichaId, varianteAtiva],
    queryFn: async () =>
      (await api.get(`/industrial/fichas/${fichaId}/composicao`, { params: { variante: varianteAtiva } })).data,
    enabled: !!fichaId,
  })

  const { data: custo } = useQuery<CustoInfo>({
    queryKey: ['ficha-custo', fichaId, varianteAtiva],
    queryFn: async () =>
      (await api.get(`/industrial/fichas/${fichaId}/custo`, { params: { variante: varianteAtiva } })).data,
    enabled: !!fichaId,
  })

  const removerIngrediente = useMutation({
    mutationFn: async (cid: number) =>
      (await api.delete(`/industrial/fichas/${fichaId}/composicao/${cid}`)).data,
    onSuccess: () => {
      toast.success('Ingrediente removido')
      qc.invalidateQueries({ queryKey: ['ficha-comp', fichaId] })
      qc.invalidateQueries({ queryKey: ['ficha-custo', fichaId] })
      qc.invalidateQueries({ queryKey: ['ficha-variantes', fichaId] })
    },
  })

  if (!ficha) {
    return <div className="p-6 text-ink-500">Carregando ficha…</div>
  }

  // Variantes conhecidas + PADRAO fallback
  const variantesList = variantes.length > 0
    ? variantes.map((v) => v.variante)
    : ['PADRAO']
  const variantesUnicas = Array.from(new Set([...variantesList, 'PADRAO', 'COR', 'PRETO']))

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/industrial/fichas" className="text-xs text-ink-500 hover:text-brand-600 flex items-center gap-1 mb-1">
            <ArrowLeft size={12} /> Voltar pra lista de fichas
          </Link>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="text-brand-600" /> {ficha.descricao || 'Ficha técnica'}
          </h1>
          <div className="text-xs text-ink-500 mt-1 flex items-center gap-3">
            <span>Ficha #{ficha.id}</span>
            <span>Versão {ficha.versao} rev. {ficha.revisao}</span>
            <span>Rendimento: {Number(ficha.rendimento_kg).toFixed(2)} kg</span>
            <span>Tempo: {ficha.tempo_producao_min}min</span>
            {ficha.ativa ? (
              <span className="text-emerald-700 font-medium">ATIVA</span>
            ) : (
              <span className="text-ink-500">INATIVA</span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs de variantes */}
      <div className="flex gap-1 border-b border-ink-200 items-end">
        {variantesUnicas.map((v) => {
          const vInfo = variantes.find((x) => x.variante === v)
          const existe = !!vInfo
          return (
            <button
              key={v}
              onClick={() => setVarianteAtiva(v)}
              className={`px-4 py-2 text-sm font-medium transition flex items-center gap-2 ${
                varianteAtiva === v
                  ? 'text-brand-700 border-b-2 border-brand-600 -mb-[2px]'
                  : 'text-ink-500 hover:text-ink-700'
              } ${!existe ? 'opacity-60' : ''}`}
            >
              {v}
              {existe && (
                <span className="text-[10px] bg-ink-100 text-ink-700 px-1.5 py-0.5 rounded">
                  {vInfo.qtd_ingredientes}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-ink-700">
          Composição — {varianteAtiva}
        </div>
        <div className="flex gap-2">
          {ingredientes.length === 0 && variantes.length > 0 && (
            <button className="btn-ghost text-xs" onClick={() => setCopiarModal(true)}>
              <Copy size={12} /> Copiar de outra variante
            </button>
          )}
          <button className="btn-primary text-xs" onClick={() => setAddModal(true)}>
            <Plus size={12} /> Adicionar ingrediente
          </button>
        </div>
      </div>

      {/* Card de custo */}
      {custo && custo.qtd_ingredientes > 0 && (
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calculator size={16} className="text-emerald-700" />
            <h3 className="font-semibold text-sm">Cálculo de custo — {varianteAtiva}</h3>
          </div>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div>
              <div className="text-xs text-ink-500 uppercase">Qtde total ingredientes</div>
              <div className="font-bold text-ink-900">{Number(custo.qtd_total_kg).toFixed(2)} kg</div>
            </div>
            <div>
              <div className="text-xs text-ink-500 uppercase">Custo total da carga</div>
              <div className="font-bold text-ink-900">R$ {Number(custo.custo_total).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-ink-500 uppercase">Rendimento</div>
              <div className="font-bold text-ink-900">{Number(custo.rendimento_kg).toFixed(2)} kg</div>
            </div>
            <div>
              <div className="text-xs text-ink-500 uppercase">Custo por kg</div>
              <div className="font-bold text-emerald-700 text-base">
                R$ {Number(custo.custo_por_kg_base_rendimento).toFixed(4)}/kg
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de ingredientes */}
      {isLoading && <div className="text-center py-6 text-ink-400">Carregando…</div>}
      {!isLoading && ingredientes.length === 0 && (
        <div className="bg-white border-2 border-dashed border-ink-200 rounded-lg p-10 text-center">
          <Beaker className="mx-auto mb-3 text-ink-300" size={40} />
          <h3 className="font-semibold text-ink-700 mb-1">
            Sem ingredientes em "{varianteAtiva}"
          </h3>
          <p className="text-xs text-ink-500 mb-4">
            Adicione ingredientes ou copie de outra variante existente.
          </p>
          <div className="flex gap-2 justify-center">
            <button className="btn-primary text-xs" onClick={() => setAddModal(true)}>
              <Plus size={12} /> Adicionar
            </button>
            {variantes.length > 0 && (
              <button className="btn-ghost text-xs" onClick={() => setCopiarModal(true)}>
                <Copy size={12} /> Copiar de outra
              </button>
            )}
          </div>
        </div>
      )}

      {ingredientes.length > 0 && (
        <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink-50">
              <tr>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Código</th>
                <th className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2">Matéria-prima</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Qtd</th>
                <th className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2">Un.</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">R$/un</th>
                <th className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2">Custo</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {ingredientes.map((i) => (
                <tr key={i.id} className="hover:bg-ink-50">
                  <td className="px-3 py-2 font-mono text-xs">{i.mp_codigo}</td>
                  <td className="px-3 py-2 font-medium">{i.mp_nome}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{Number(i.quantidade).toFixed(4)}</td>
                  <td className="px-3 py-2 text-center text-xs">{i.unidade}</td>
                  <td className="px-3 py-2 text-right text-xs">{Number(i.custo_unitario).toFixed(4)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                    R$ {Number(i.custo_total).toFixed(2)}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => pedirExclusao({ descricao: `o item da composição "${i.mp_nome}"`, onConfirm: () => removerIngrediente.mutate(i.id) })}
                      className="text-ink-500 hover:text-red-600 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addModal && (
        <AddIngredienteModal
          fichaId={fichaId}
          varianteAtual={varianteAtiva}
          onClose={() => setAddModal(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['ficha-comp', fichaId] })
            qc.invalidateQueries({ queryKey: ['ficha-custo', fichaId] })
            qc.invalidateQueries({ queryKey: ['ficha-variantes', fichaId] })
            setAddModal(false)
          }}
        />
      )}

      {copiarModal && (
        <CopiarComposicaoModal
          fichaId={fichaId}
          varianteAtual={varianteAtiva}
          variantesDisponiveis={variantes.filter((v) => v.variante !== varianteAtiva).map((v) => v.variante)}
          onClose={() => setCopiarModal(false)}
          onSuccess={() => {
            qc.invalidateQueries({ queryKey: ['ficha-comp', fichaId] })
            qc.invalidateQueries({ queryKey: ['ficha-custo', fichaId] })
            qc.invalidateQueries({ queryKey: ['ficha-variantes', fichaId] })
            setCopiarModal(false)
          }}
        />
      )}
    </div>
  )
}


// ============================================================
// Modal adicionar ingrediente
// ============================================================
function AddIngredienteModal({
  fichaId, varianteAtual, onClose, onSuccess,
}: { fichaId: number; varianteAtual: string; onClose: () => void; onSuccess: () => void }) {
  const [busca, setBusca] = useState('')
  const [mpSel, setMpSel] = useState<{ id: number; codigo: string; nome: string; unidade: string; custo_unitario: string } | null>(null)
  const [qtd, setQtd] = useState(0)
  const [perda, setPerda] = useState(0)

  const { data: mps = [] } = useQuery<any[]>({
    queryKey: ['mps-busca', busca],
    queryFn: async () =>
      (await api.get('/industrial/materias-primas', { params: { q: busca || undefined } })).data,
    enabled: busca.length > 0 || !mpSel,
  })

  const mut = useMutation({
    mutationFn: async () => {
      if (!mpSel) throw new Error('MP não selecionada')
      return (await api.post(`/industrial/fichas/${fichaId}/composicao`, {
        variante: varianteAtual,
        materia_prima_id: mpSel.id,
        quantidade: qtd,
        unidade: mpSel.unidade,
        perda_pct: perda,
      })).data
    },
    onSuccess: () => {
      toast.success('Ingrediente adicionado')
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao adicionar'),
  })

  const custoEstimado = mpSel ? qtd * Number(mpSel.custo_unitario) : 0

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-xl w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Plus size={18} /> Adicionar ingrediente em {varianteAtual}
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          {!mpSel ? (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  className="input pl-9 w-full" autoFocus
                  placeholder="Buscar MP por código ou nome..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  onKeyDown={enterAbrePrimeiro(mps.slice(0, 30), (m) => setMpSel({ id: m.id, codigo: m.codigo, nome: m.nome, unidade: m.unidade, custo_unitario: m.custo_unitario }))}
                />
              </div>
              <div className="max-h-64 overflow-y-auto border border-ink-100 rounded">
                {mps.slice(0, 30).map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setMpSel({ id: m.id, codigo: m.codigo, nome: m.nome, unidade: m.unidade, custo_unitario: m.custo_unitario })}
                    className="w-full text-left px-3 py-2 hover:bg-brand-50 border-b border-ink-100 flex items-center justify-between"
                  >
                    <div>
                      <span className="font-mono text-xs text-ink-500 mr-2">{m.codigo}</span>
                      <span className="font-medium">{m.nome}</span>
                    </div>
                    <div className="text-xs text-ink-500">
                      R$ {Number(m.custo_unitario).toFixed(4)}/{m.unidade}
                    </div>
                  </button>
                ))}
                {mps.length === 0 && <div className="text-center py-6 text-ink-400 text-sm">Nenhuma MP encontrada</div>}
              </div>
            </>
          ) : (
            <>
              <div className="bg-brand-50 border border-brand-200 rounded p-3">
                <div className="text-xs text-ink-500">MP selecionada:</div>
                <div className="font-semibold">{mpSel.codigo} — {mpSel.nome}</div>
                <div className="text-xs text-ink-600">R$ {Number(mpSel.custo_unitario).toFixed(4)}/{mpSel.unidade}</div>
                <button onClick={() => setMpSel(null)} className="text-xs text-brand-600 mt-1 hover:underline">
                  ← Trocar MP
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Quantidade ({mpSel.unidade}) *</label>
                  <input type="number" step="0.0001" autoFocus className="input" value={qtd || ''}
                    onChange={(e) => setQtd(Number(e.target.value))} />
                </div>
                <div>
                  <label className="label">Perda %</label>
                  <input type="number" step="0.01" className="input" value={perda || ''}
                    onChange={(e) => setPerda(Number(e.target.value))} />
                </div>
              </div>
              {qtd > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-2 rounded flex items-center gap-2">
                  <Calculator size={14} />
                  Custo desse ingrediente: <strong>R$ {custoEstimado.toFixed(2)}</strong>
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
                <button className="btn-primary flex-1" disabled={qtd <= 0 || mut.isPending}
                  onClick={() => mut.mutate()}>
                  {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Adicionar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}


// ============================================================
// Modal copiar composição de outra variante
// ============================================================
function CopiarComposicaoModal({
  fichaId, varianteAtual, variantesDisponiveis, onClose, onSuccess,
}: { fichaId: number; varianteAtual: string; variantesDisponiveis: string[]; onClose: () => void; onSuccess: () => void }) {
  const [origem, setOrigem] = useState(variantesDisponiveis[0] || 'PADRAO')

  const mut = useMutation({
    mutationFn: async () =>
      (await api.post(`/industrial/fichas/${fichaId}/composicao/copiar`, null, {
        params: {
          variante_origem: origem,
          variante_destino: varianteAtual,
        },
      })).data,
    onSuccess: (r) => {
      toast.success(`${r.copiadas} ingrediente(s) copiado(s)`)
      onSuccess()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao copiar'),
  })

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-ink-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Copy size={18} /> Copiar composição
          </h2>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-sm text-ink-600">
            Copiar todos os ingredientes de uma variante pra <strong>{varianteAtual}</strong>.
          </div>
          <div>
            <label className="label">Copiar de (origem)</label>
            <select className="input" value={origem} onChange={(e) => setOrigem(e.target.value)}>
              {variantesDisponiveis.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2 rounded flex items-start gap-1">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>A variante destino deve estar vazia. Remova ingredientes manualmente se já houver.</span>
          </div>
          <div className="flex gap-2 pt-2">
            <button className="btn-ghost flex-1" onClick={onClose}>Cancelar</button>
            <button className="btn-primary flex-1" disabled={mut.isPending}
              onClick={() => mut.mutate()}>
              {mut.isPending ? <Loader2 className="animate-spin" size={14} /> : 'Copiar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
